import React, { useState } from "react";
import {
  GMBLocation,
  CustomerOption,
  SaveGMBTokenParams,
} from "../types/gmb.types";
import { saveGMBTokens } from "../services/gmbAuthentication";
import LocationCard from "./LocationCard";
import { Platform } from "../../../../../types/Platforms";
import { useAuth } from "../../../../../context/AuthContext";

interface BusinessLocationSelectionProps {
  locations: GMBLocation[];
  refreshToken: string;
  email: string;
  customers: CustomerOption[];
  onClose: () => void;
}

const BusinessLocationSelection: React.FC<BusinessLocationSelectionProps> = ({
  locations,
  refreshToken,
  email,
  customers,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomerIdByLocationId, setSelectedCustomerIdByLocationId] = useState<
    Record<string, string>
  >({});
  const [error, setError] = useState<string | null>(null);
  const { idToken } = useAuth();
  const [connectedLocations, setConnectedLocations] = useState<Record<string, boolean>>({});
  const [savingLocationId, setSavingLocationId] = useState<string | null>(null);

  const filteredLocations = locations.filter(
    (location) =>
      location.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = async (location: GMBLocation, customerId: string) => {
    setSavingLocationId(location.locationId);
    setError(null);

    const tokenData: SaveGMBTokenParams = {
      customer_id: customerId,
      platform: Platform.GOOGLE_MY_BUSINESS,
      created_by: email,
      profiles: [
        {
          id: location.locationId,
          token: refreshToken,
          metadata: {
            name: location.title,
            address: location.address,
            token_type: "refresh_token",
            account_id: location.accountId,
          },
        },
      ],
    };

    try {
      await saveGMBTokens(tokenData, idToken ?? "");
      setConnectedLocations((prev) => ({
        ...prev,
        [location.locationId]: true,
      }));
    } catch (err) {
      setError("Failed to connect this location. Please try again.");
    } finally {
      setSavingLocationId(null);
    }
  };

  return (
    <>
      <header className="mb-8 pb-8 border-b border-gray-100">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
          Connect Google Business Profile
        </h1>
        <p className="text-sm text-gray-500">
          Manage and connect your Google Business Profile locations.
        </p>
      </header>

      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">
          Available Google Business Profile locations
        </h2>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search locations"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:ring-brand-primary focus:border-brand-primary text-sm"
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {filteredLocations.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No locations found. Try a different search term.</p>
      ) : (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {filteredLocations.map((location) => (
            <LocationCard
              key={location.locationId}
              location={location}
              customers={customers}
              selectedCustomerId={selectedCustomerIdByLocationId[location.locationId] ?? ""}
              onCustomerChange={(customerId) =>
                setSelectedCustomerIdByLocationId((prev) => ({
                  ...prev,
                  [location.locationId]: customerId,
                }))
              }
              onAdd={handleAdd}
              isConnected={!!connectedLocations[location.locationId]}
              isSaving={savingLocationId === location.locationId}
            />
          ))}
        </section>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
        >
          Done
        </button>
      </div>
    </>
  );
};

export default BusinessLocationSelection;
